// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { FormItem, IFormInstance, useWatchForm } from '@/components';
import { Button, Input, Select } from '@/ui';
import { StyledDatePicker, StyledForm, StyledTitle, StyledWrapper } from './ProductSyncFormStyles';

// Helpers
import { getChannelsOptions, getSyncOptions, SyncFormFormFields, SyncType, TSyncFormValues } from '../../helpers';
import { useSyncFormRules } from '../../hooks';

// Models
import { IChannel } from '@/models/channel.model';

type TProductSyncFormProps = {
  productSyncForm: IFormInstance;
  onSubmit: (values: TSyncFormValues) => void;
  channels: IChannel[];
  currentChannelSlug?: string;
};

const ProductSyncForm = ({ onSubmit, productSyncForm, currentChannelSlug, channels }: TProductSyncFormProps) => {
  const { t } = useTranslation();
  const rules = useSyncFormRules(productSyncForm);
  const syncTypeOptions = useMemo(() => getSyncOptions(t), [t]);
  const channelsOptions = useMemo(() => getChannelsOptions(channels), [channels]);
  const syncType = useWatchForm(SyncFormFormFields.TYPE, productSyncForm);

  const isSpecificProductSync = syncType === SyncType.SPECIFIC_PRODUCT;

  const handleChangeSyncType = (productsSyncType: SyncType) => {
    if (productsSyncType === SyncType.SPECIFIC_PRODUCT) {
      productSyncForm.setFieldValue(SyncFormFormFields.CHANNEL, currentChannelSlug);
      productSyncForm.setFieldValue(SyncFormFormFields.SKU, null);
    }
  };

  return (
    <StyledWrapper>
      <StyledTitle>{t('title_products_sync')}</StyledTitle>
      <StyledForm onFinish={onSubmit} form={productSyncForm}>
        <FormItem name={SyncFormFormFields.TYPE} label={t('label_what_to_sync')} rules={rules[SyncFormFormFields.TYPE]}>
          <Select showSearch onChange={handleChangeSyncType} options={syncTypeOptions} allowClear />
        </FormItem>
        {isSpecificProductSync ? (
          <>
            <FormItem
              name={SyncFormFormFields.CHANNEL}
              label={t('label_channels')}
              rules={rules[SyncFormFormFields.CHANNEL]}
            >
              <Select showSearch options={channelsOptions} />
            </FormItem>
            <FormItem
              name={SyncFormFormFields.SKU}
              label={t('label_product_sku')}
              rules={rules[SyncFormFormFields.SKU]}
            >
              <Input placeholder={t('placeholder_product_sku')} />
            </FormItem>
          </>
        ) : (
          <>
            <FormItem
              name={SyncFormFormFields.CHANNEL_SLUGS}
              label={t('label_channels')}
              rules={rules[SyncFormFormFields.CHANNEL_SLUGS]}
            >
              <Select isMultiselect options={channelsOptions} allowClear />
            </FormItem>
            <FormItem
              name={SyncFormFormFields.UPDATED_FROM}
              label={t('label_date_from')}
              rules={rules[SyncFormFormFields.UPDATED_FROM]}
            >
              <StyledDatePicker allowClear />
            </FormItem>
            <FormItem
              name={SyncFormFormFields.UPDATED_UNTIL}
              label={t('label_date_to')}
              rules={rules[SyncFormFormFields.UPDATED_UNTIL]}
            >
              <StyledDatePicker allowClear />
            </FormItem>
          </>
        )}
        <Button block type="primary" htmlType="submit">
          {t('button_sync_products')}
        </Button>
      </StyledForm>
    </StyledWrapper>
  );
};

export default ProductSyncForm;
